import { createBaseExportSchema } from '@app/modules/social-export/components/validation/useBaseExportSchema';
import { useMemo } from 'react';
import { IntlFormatters, useIntl } from 'react-intl';
import { z } from 'zod';
import { tiktokSettingsSchema } from './TikTokSettingsForm';

const TIKTOK_TITLE_MAX_LENGTH = 2200;

const disclosureRequiredMsg = {
  id: 'socialExport.tiktok.disclosure.selectAtLeastOne',
  defaultMessage: 'Indica si el contenido promociona tu marca, a un tercero o a ambos.',
};

/** Builds the Zod schema for the TikTok publish form. */
export function createTiktokPublishSchema(t: IntlFormatters['$t']) {
  return createBaseExportSchema(t)
    .merge(tiktokSettingsSchema)
    .extend({
      title: z.string().trim().min(1).max(TIKTOK_TITLE_MAX_LENGTH),
      thumbType: z.enum(['DEFAULT', 'VIDEO_FRAME']),
      coverOffsetMs: z.number().nullable().optional(),
      discloseVideoContent: z.boolean().optional(),
      brandContent: z.boolean().optional(),
      brandOrganic: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.discloseVideoContent && !data.brandContent && !data.brandOrganic) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(disclosureRequiredMsg),
          path: ['brandOrganic'],
        });
      }
    });
}

export type TikTokPublishValues = z.infer<ReturnType<typeof createTiktokPublishSchema>>;

/** Zod schema for the TikTok publish form with translated validation messages. */
export function useTiktokPublishSchema() {
  const { $t } = useIntl();
  return useMemo(() => createTiktokPublishSchema($t), [$t]);
}
